import React, { useEffect, useMemo, useState, useRef, memo } from "react";
import {
	FaCheck,
	FaEraser,
	FaFloppyDisk,
	FaPenToSquare,
	FaTriangleExclamation,
	FaUser,
} from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import Fuse from "fuse.js";
import "./user-profile-sidemodal.style.scss";

// for filter form handle
import { useFormik } from "formik";
import * as yup from "yup";

// for date purpose
import moment from "moment";

// components
import StructuredSideModal from "../structured-side-modal/structured-side-modal.component";
import { Button } from "../ui/button";
import SelectBox from "../form-inputs/select-box.component";
import TextField from "../form-inputs/text-field.component";
import DisplayField, { getOptionLabel } from "../form-inputs/display-field.component";
import MultipleTagsInput from "../form-inputs/multiple-tags-input.component";
import InlineNotice from "../inline-notice/inline-notice.component";
import Spinner from "../spinner/spinner.component";

// global strings and functions
import strings from "../../global/strings";
import { PHONE_REGEX, PHONE_REGEX_MESSAGE } from "../../global/phone.const";
import {
	fromAdToBs,
	isEmptyObject,
	rupeeConverter,
} from "../../global/function";
import { queryClient } from "../../global/query-client";
import { useDebounce } from "../../hooks/useDebounce";
import {
	Customer,
	customersQueryKey,
	useAddCustomer,
	useUpdateCustomer,
	useCustomerDetails,
	useFilteredCustomers,
} from "../../queries/customer.queries";
import { useUploadImage, UploadImageResult } from "../../queries/image.queries";
import FormError from "../form-error/form-error.component";
import SuccessDisplay from "../success-display/success-display.component";

interface CustomerProfileProps {
	isCustomerVisible: boolean;
	onCustomerModalClose: () => void;
	customer: Partial<Customer>;
	currentPageNum?: number;
	// `options.openEdit` is set when the caller picked "Complete Profile" on
	// the quick-create success screen — callers that don't care (view/edit
	// instances, invoice-form's buyer picker) can ignore the second argument
	// entirely, same as today.
	// `options.isProfileUpdate` is set when this fires from saving an edit to
	// an *already-linked* customer (the update-mutation branch below) rather
	// than a new selection — invoice-form's buyer picker uses it to tell "the
	// linked customer's data changed" apart from "a customer was just picked,"
	// since only the latter should seed bill_for tags / count as a bill edit.
	onSelectCustomer: (
		customer: Partial<Customer>,
		options?: { openEdit?: boolean; isProfileUpdate?: boolean },
	) => void;
	// Opens directly in edit mode — used by the "Complete Profile" handoff
	// right after a successful quick-create, so the user lands straight into
	// filling in the fields (type/gender/PAN-VAT/address/city) the quick-create
	// step deferred, instead of having to click Edit themselves.
	startEditable?: boolean;
}

// The formik values object ends up holding the full Customer row once
// formik.setValues({ ...initialValues, ...customer }) merges the `customer`
// prop in (see the effect below) — including fields the initial 9-key literal
// below doesn't declare (customer_id, company_customer_id, customer_image,
// etc.), which onSubmit deletes/reassigns. Partial<Customer> covers both.
type CustomerFormValues = Partial<Customer>;

const customerSchema = yup.object().shape({
	customer_name: yup
		.string()
		.nullable()
		.required("Customer Name is required"),
	customer_mobile: yup
		.string()
		.matches(PHONE_REGEX, PHONE_REGEX_MESSAGE)
		.nullable()
		.required("Customer Mobile is required"),
	customer_type: yup.string().nullable(),
	customer_email: yup.string().email("Please enter valid email").nullable(),
	// .required("Customer Email is required"),
	customer_pan_vat: yup
		.string()
		.nullable()
		.when("customer_type", {
			is: (value: string) => value === "business",
			then: () =>
				yup
					.string()
					.matches(/^[0-9]*$/, "Please enter a valid number")
					.required("PAN / VAT number required"),
		}),
});

function CustomerProfile({
	isCustomerVisible,
	onCustomerModalClose,
	customer,
	currentPageNum,
	onSelectCustomer,
	startEditable = false,
}: CustomerProfileProps) {
	// Self-contained to this modal — no other page reads customer bill
	// details, so this stays off Redux entirely (see useCustomerDetails).
	const { data: customerDetails, isLoading } = useCustomerDetails(
		customer?.company_customer_id || undefined,
	);
	const [clientEditable, setClientEditable] = useState(startEditable);
	const [selectedImage, setSelectedImage] = useState<File | null>(null);
	const [imageSizeNotice, setImageSizeNotice] = useState<string | null>(null);

	// customer tags to store temporarily
	const [tags, setTags] = useState<string[]>([]);

	// Holds the newly-created customer between a successful quick-create and
	// whichever of the two success-screen buttons the user picks — see
	// handleSkipForNow/handleCompleteProfile below.
	const [addedCustomer, setAddedCustomer] = useState<
		(Partial<Customer> & { isNew?: boolean }) | null
	>(null);

	const navigate = useNavigate();
	const fileInputRef = useRef<HTMLInputElement>(null);

	// company_customer_id === "" is this file's existing sentinel for "adding
	// a brand new customer" (see handleEditCancel/useEffect/render below).
	const isAddMode = customer.company_customer_id === "";

	const addCustomerMutation = useAddCustomer();
	const updateCustomerMutation = useUpdateCustomer();
	const uploadImageMutation = useUploadImage();
	const activeMutation = customer?.company_customer_id
		? updateCustomerMutation
		: addCustomerMutation;

	// Update still shows a brief SuccessDisplay before auto-closing/reverting
	// to view mode. Add no longer auto-closes — addedCustomer being set is
	// what drives the two-button (Complete Profile / Skip for now) screen
	// instead, see onSubmit and handleSkipForNow/handleCompleteProfile below.
	// updateCustomerMutation.isSuccess alone isn't enough — a duplicate-record
	// response now also resolves (rather than throws, see putUpdateCustomer),
	// so this also checks the resolved data's own success flag.
	const showSuccess = customer?.company_customer_id
		? updateCustomerMutation.isSuccess &&
			updateCustomerMutation.data?.success
		: Boolean(addedCustomer);
	const successTitle = customer?.company_customer_id
		? "Customer updated"
		: "Customer added";
	const successDescription = customer?.company_customer_id
		? "Customer details updated successfully."
		: "Add more details now, or fill them in anytime from their profile.";
	const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);
	useEffect(() => {
		return () => {
			if (successTimeoutRef.current)
				clearTimeout(successTimeoutRef.current);
		};
	}, []);

	function convertTagToString(array: string[]) {
		const string = "#" + array.join("#");
		return string;
	}

	function convertTagToArray(string?: string) {
		const array = string?.split("#").filter(Boolean);
		return array;
	}

	const formik = useFormik<CustomerFormValues>({
		validationSchema: customerSchema,
		initialValues: {
			customer_pan_vat: "",
			customer_name: "",
			customer_email: "",
			customer_mobile: "",
			customer_gender: "",
			customer_type: "",
			customer_city: "",
			customer_address: "",
			customer_tag: "",
		},
		onSubmit: async (customerData) => {
			if (customerData.customer_type === "business") {
				customerData["customer_gender"] = "other";
			}
			customerData["customer_gender"] =
				customerData.customer_gender === ""
					? null
					: customerData.customer_gender;
			customerData["customer_type"] =
				customerData.customer_type === ""
					? "individual"
					: customerData.customer_type;
			customerData["customer_pan_vat"] =
				customerData.customer_pan_vat === ""
					? null
					: customerData.customer_pan_vat;
			// function called to convert tags array to string
			customerData["customer_tag"] =
				Array.isArray(tags) && tags.length > 0
					? convertTagToString(tags)
					: "";
			delete customerData.customer_image;

			if (!customerData?.company_customer_id) {
				//when no customer is selected ADD new customer
				delete customerData.customer_id;
				delete customerData.company_customer_id;
				delete customerData.company_info_id;

				let response;
				try {
					response =
						await addCustomerMutation.mutateAsync(customerData);
				} catch {
					return; // FormError (bound to addCustomerMutation) renders the message
				}

				if (response?.success) {
					// Image upload failure doesn't block customer creation —
					// the customer is already saved, only the avatar is missing.
					const uploadResponse: UploadImageResult | undefined =
						selectedImage
							? await uploadImageMutation
									.mutateAsync({
										image: selectedImage,
										id: response.result.company_customer_id,
										type: "companyCustomer",
									})
									.catch((): undefined => undefined)
							: undefined;

					const newCustomer = {
						...response.result,
						customer_image:
							uploadResponse?.result?.customer_image || null,
						isNew: true,
					};
					// customer-list.page.tsx and invoice-form's buyer autocomplete
					// both read this query cache now (customerReducer is gone) —
					// invalidate so the new customer shows up in both.
					queryClient.invalidateQueries({
						queryKey: customersQueryKey,
					});

					// Drives the Complete Profile / Skip for now success screen —
					// see handleSkipForNow/handleCompleteProfile below. No more
					// auto-close timeout: the user picks one of the two actions.
					setAddedCustomer(newCustomer);
				} else if (response?.duplication) {
					//condition for duplicate record found
					const { email, mobile, pan_vat } = response.duplication;
					mobile &&
						formik.setFieldError(
							"customer_mobile",
							"Duplicate record",
						);
					pan_vat &&
						formik.setFieldError(
							"customer_pan_vat",
							"Duplicate record",
						);
					email &&
						formik.setFieldError(
							"customer_email",
							"Duplicate record",
						);
				}
			} else {
				//when customer is selected, update the customer
				let uploadResponse: UploadImageResult | undefined;
				let uploadResult = true;
				if (selectedImage) {
					try {
						uploadResponse = await uploadImageMutation.mutateAsync({
							image: selectedImage,
							id: customerData.company_customer_id,
							type: "companyCustomer",
						});
					} catch {
						uploadResult = false;
					}
				}
				if (uploadResult) {
					let updateResult;
					try {
						updateResult = await updateCustomerMutation.mutateAsync(
							{
								customerData,
								page: currentPageNum,
							},
						);
					} catch {
						return; // FormError (bound to updateCustomerMutation) renders the message
					}

					if (updateResult.duplication) {
						//condition for duplicate record found — same field-error
						//pattern as the add branch above
						const { email, mobile, pan_vat } =
							updateResult.duplication;
						mobile &&
							formik.setFieldError(
								"customer_mobile",
								"Duplicate record",
							);
						pan_vat &&
							formik.setFieldError(
								"customer_pan_vat",
								"Duplicate record",
							);
						email &&
							formik.setFieldError(
								"customer_email",
								"Duplicate record",
							);
						return;
					}

					queryClient.invalidateQueries({
						queryKey: customersQueryKey,
					});

					successTimeoutRef.current = setTimeout(() => {
						updateCustomerMutation.reset();
						onSelectCustomer(
							{
								...customerData,
								customer_image:
									uploadResponse?.result?.customer_image || null,
							},
							{ isProfileUpdate: true },
						);
						setClientEditable(false);
						// Rebase the dirty-comparison baseline to what was just
						// saved — otherwise formik.dirty stays true forever
						// after a successful save (initialValues still points at
						// the pre-edit customer), which kept wrongly triggering
						// the discard-changes prompt on close/re-edit even
						// though there was nothing left to discard.
						formik.resetForm({ values: formik.values });
					}, 1500);
				}
			}
		},
	});

	// Pre-submit duplicate check while adding — searches existing customers as
	// the name/mobile fields are typed, so the user can spot a likely-existing
	// customer before submitting. Unlike the add-user wizard, selecting a match
	// here doesn't "link" anything (customers have no cross-company sharing
	// concept) — it just reuses onSelectCustomer, exactly as if the match had
	// been picked from the invoice-form buyer autocomplete, and closes this
	// modal instead of creating a duplicate. The BE's own duplication check
	// (see the "duplicate record" field-error branch above) still runs as the
	// authoritative final gate on submit — this is purely an earlier, softer
	// heads-up.
	const debouncedCustomerName = useDebounce(
		formik.values.customer_name || "",
		400,
	);
	const debouncedCustomerMobile = useDebounce(
		formik.values.customer_mobile || "",
		400,
	);
	const namePrefix = debouncedCustomerName.trim().slice(0, 2).toLowerCase();
	const mobilePrefix = debouncedCustomerMobile
		.trim()
		.replace(/\D/g, "")
		.slice(0, 4);
	const hasMatchQuery = namePrefix.length >= 2 || mobilePrefix.length >= 4;

	// Queried separately (not combined in one filter object) because
	// customer/search's where-clause ANDs every filter it's given — sending
	// name+mobile together would require BOTH to match the same record, when
	// the point here is "does either field alone match something existing."
	const { data: nameMatchBatch, isFetching: isSearchingByName } =
		useFilteredCustomers(
			{ customer_name: namePrefix },
			isAddMode && namePrefix.length >= 2,
		);
	const { data: mobileMatchBatch, isFetching: isSearchingByMobile } =
		useFilteredCustomers(
			{ customer_mobile: mobilePrefix },
			isAddMode && mobilePrefix.length >= 4,
		);
	const isSearchingMatches = isSearchingByName || isSearchingByMobile;

	// Fuzzy (typo-tolerant) match against the name batch only — mobile numbers
	// don't need fuzz, a plain substring check on the mobile batch below is
	// exact and simpler. Kept as two independent tracks, then unioned, rather
	// than one combined Fuse query — a single query string can't represent
	// "matches this name OR this mobile" at once.
	const nameFuse = useMemo(() => {
		if (!nameMatchBatch || nameMatchBatch.length === 0) return null;
		return new Fuse(nameMatchBatch, {
			shouldSort: true,
			includeScore: true,
			useExtendedSearch: true,
			keys: ["customer_name"],
		});
	}, [nameMatchBatch]);

	const customerMatches: Customer[] = useMemo(() => {
		const typedName = (formik.values.customer_name || "").trim();
		const normalizedTypedMobile = (formik.values.customer_mobile || "")
			.trim()
			.replace(/\D/g, "");

		const nameMatches =
			typedName.length >= 2 && nameFuse
				? nameFuse.search(typedName).map((r) => r.item)
				: [];
		const mobileMatches =
			normalizedTypedMobile.length >= 4
				? (mobileMatchBatch ?? []).filter((c) =>
						(c.customer_mobile || "")
							.replace(/\D/g, "")
							.includes(normalizedTypedMobile),
					)
				: [];

		const seen = new Set<string>();
		return [...nameMatches, ...mobileMatches].filter((c) => {
			if (seen.has(c.company_customer_id)) return false;
			seen.add(c.company_customer_id);
			return true;
		});
	}, [
		nameFuse,
		mobileMatchBatch,
		formik.values.customer_name,
		formik.values.customer_mobile,
	]);

	// Exact phone-number match — unlike CaterMe, billing_system_BE's
	// customer/add hard-rejects an exact mobile match unconditionally
	// (helper_duplicateCompanyCustomer, scoped per company, no override flag),
	// so there's no "create anyway" path that could ever succeed. Rather than
	// offer a button that's guaranteed to fail on submit, this just blocks
	// Save outright and points at the existing record below.
	const normalizedTypedMobile = (formik.values.customer_mobile || "").replace(
		/\D/g,
		"",
	);
	const exactPhoneMatches = customerMatches.filter(
		(m) =>
			normalizedTypedMobile.length > 0 &&
			(m.customer_mobile || "").replace(/\D/g, "") ===
				normalizedTypedMobile,
	);
	const submitDisabled = isAddMode && exactPhoneMatches.length > 0;

	const handleSelectExistingCustomer = (matched: Customer) => {
		onSelectCustomer(matched);
		onCustomerModalClose();
	};

	const handleSkipForNow = () => {
		addCustomerMutation.reset();
		if (addedCustomer) onSelectCustomer(addedCustomer);
		setClientEditable(false);
		setAddedCustomer(null);
		onCustomerModalClose();
	};

	const handleCompleteProfile = () => {
		addCustomerMutation.reset();
		if (addedCustomer) onSelectCustomer(addedCustomer, { openEdit: true });
		setClientEditable(false);
		setAddedCustomer(null);
		onCustomerModalClose();
	};

	const handleEditCancel = () => {
		setClientEditable(false);
		setSelectedImage(null);
		// resetForm (not setValues) rebases formik's dirty-comparison baseline
		// back to the original customer — otherwise dirty would stay true after
		// canceling an edit (formik.dirty backs the discard-changes guard on
		// StructuredSideModal below).
		formik.resetForm({
			values: {
				...formik.initialValues,
				...customer,
			},
		});
		customer.company_customer_id === "" && onCustomerModalClose();
	};

	useEffect(() => {
		if (!isEmptyObject(customer)) {
			customer.company_customer_id === "" && setClientEditable(true);
			// set tag state to display tags
			customer.customer_tag &&
				setTags(convertTagToArray(customer.customer_tag) || []);
			// resetForm (not setValues) rebases formik's dirty-comparison
			// baseline to this freshly-loaded customer — otherwise dirty would
			// read true the moment this populates, before the user touches
			// anything.
			formik.resetForm({
				values: {
					...formik.initialValues,
					...customer,
				},
			});
		}
		// eslint-disable-next-line
	}, []);

	function handleTagAdd(
		e:
			| React.FocusEvent<HTMLInputElement>
			| React.KeyboardEvent<HTMLInputElement>,
	) {
		const target = e.target as HTMLInputElement;
		// Get the value of the input
		const value = target.value.toLowerCase();
		// If the value is empty, return
		if (!value.trim()) return;
		// cleaning up all hash values
		const newValue: string[] = value?.split("#").filter(Boolean);

		// Convert the arrays to Sets to remove duplicates
		const set1 = new Set(tags);
		const set2 = new Set(newValue);

		// Merge the Sets
		const mergedSet = new Set<string>([...set1, ...set2]);

		// Convert the merged Set back to an array
		const resultArray = Array.from(mergedSet);

		// Add the value to the tags array
		setTags(resultArray);
		// Clear the input
		target.value = "";
	}

	function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		// If user did not press enter key, return
		if (e.key !== "Enter") return;
		handleTagAdd(e);
	}

	function handleTagRemove(index: number) {
		setTags(tags.filter((el, i) => i !== index));
	}

	function handleViewAllBills(customerId?: string) {
		navigate("/invoice/bills", { state: { customerId } });
	}

	// Only reachable for an existing customer (isAddMode forces clientEditable
	// true on mount, so view mode never applies while adding) — reads straight
	// off the `customer` prop rather than formik.values, since the mount
	// effect below only ever syncs formik once ([] deps), so formik.values
	// could be stale by the time a caller re-passes an updated `customer`.
	const _renderClientInfoView = () => {
		const viewTags = convertTagToArray(customer.customer_tag) ?? [];
		return (
			<div className="user-info-container">
				<h2 className="section-header">Customer Tags</h2>
				{viewTags.length > 0 ? (
					<div className="tags-wrapper">
						{viewTags.map((tag, index) => (
							<div className="tag-container" key={index}>
								<span className="tag-item">{tag}</span>
							</div>
						))}
					</div>
				) : (
					<div className="display-value">—</div>
				)}
				<h2 className="section-header">Customer Details</h2>
				<DisplayField
					label="Customer Name"
					value={customer.customer_name}
					valueClassName="cap-display"
				/>
				<div className="input-row-wrapper">
					<DisplayField label="Mobile Number" value={customer.customer_mobile} />
					<DisplayField label="Customer Email" value={customer.customer_email} />
				</div>
				<div className="input-row-wrapper">
					<DisplayField
						label="Customer Type"
						value={getOptionLabel(strings.customerType, customer.customer_type)}
					/>
					<DisplayField
						label="Customer Gender"
						value={getOptionLabel(strings.gender, customer.customer_gender)}
					/>
				</div>
				<DisplayField label="Customer PAN / VAT" value={customer.customer_pan_vat} />
				<DisplayField label="Customer Address" value={customer.customer_address} />
				<div className="input-row-wrapper">
					<DisplayField
						label="City"
						value={getOptionLabel(strings.city, customer.customer_city)}
					/>
					<DisplayField label="Country" value="Nepal" />
				</div>
			</div>
		);
	};

	const _renderClientInfo = () => {
		if (!clientEditable) return _renderClientInfoView();

		return (
			<div className="user-info-container">
				<h2 className="section-header">Customer Tags</h2>
				<MultipleTagsInput
					label="Customer Tags"
					name="customer_tag"
					onKeyDown={handleKeyDown}
					onBlur={handleTagAdd}
					onTagRemove={handleTagRemove}
					tagsArray={tags}
					autoComplete="off"
				/>
				<h2 className="section-header">Customer Details</h2>
				<TextField
					name="customer_name"
					label="Customer Name"
					placeholder="Enter Customer Name"
					formik={formik}
					className="cap-display"
				/>
				<div className="input-row-wrapper">
					<TextField
						name="customer_mobile"
						label="Mobile Number"
						placeholder="Enter Mobile Number"
						type="tel"
						formik={formik}
					/>
					<TextField
						name="customer_email"
						label="Customer Email"
						placeholder="Enter Email"
						formik={formik}
					/>
				</div>
				{isAddMode &&
					exactPhoneMatches.length > 0 && (
						<div className="exact-match-warning">
							<FaTriangleExclamation
								size={16}
								aria-hidden="true"
							/>
							<div className="exact-match-warning__body">
								<p>
									{exactPhoneMatches.length === 1 ? (
										<>
											A customer with this phone number
											already exists:{" "}
											<strong className="cap-display">
												{
													exactPhoneMatches[0]
														.customer_name
												}
											</strong>
											. Select them below, or use a
											different mobile number.
										</>
									) : (
										<>
											{exactPhoneMatches.length} customers
											with this phone number already
											exist:{" "}
											{exactPhoneMatches.map(
												(m, index) => (
													<span
														key={
															m.company_customer_id
														}
													>
														<strong className="cap-display">
															{m.customer_name}
														</strong>
														{index <
														exactPhoneMatches.length -
															1
															? ", "
															: ""}
													</span>
												),
											)}
											. Select one below, or use a
											different mobile number.
										</>
									)}
								</p>
							</div>
						</div>
					)}
				{isAddMode && hasMatchQuery && (
					<div className="user-match-list">
						{isSearchingMatches ? (
							<div className="user-match-hint">
								<Spinner styles={undefined} />
								<span>Checking for existing customers...</span>
							</div>
						) : customerMatches.length === 0 ? (
							<div className="user-match-hint">
								No matching customers found.
							</div>
						) : (
							<>
								<p className="user-match-list-label">
									Found similar existing customers — select
									one to use it instead of creating a
									duplicate
								</p>
								{customerMatches.map((match) => (
									<button
										type="button"
										key={match.company_customer_id}
										className="user-match-item"
										onClick={() =>
											handleSelectExistingCustomer(match)
										}
									>
										<div className="user-match-avatar">
											{match.customer_image ? (
												<img
													crossOrigin="anonymous"
													src={match.customer_image}
													alt=""
												/>
											) : (
												<FaUser />
											)}
										</div>
										<div className="user-match-info">
											<span className="user-match-name cap-display">
												{match.customer_name}
											</span>
											<span className="user-match-subtitle">
												{[
													match.customer_mobile,
													match.customer_email,
												]
													.filter(Boolean)
													.join(" · ")}
											</span>
										</div>
									</button>
								))}
							</>
						)}
					</div>
				)}
				{/* Deferred to the edit pass — quick-create only collects name/
				mobile/email (matches CaterMe's add-customer quick mode); every
				new customer defaults to individual/Nepal until edited. */}
				{!isAddMode && (
					<>
						<div className="input-row-wrapper">
							<div className="input-wrapper">
								<label className="input-label">
									Customer Type
								</label>
								<SelectBox
									name="customer_type"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values?.customer_type || ""}
									options={strings.customerType}
								/>
								{formik.submitCount > 0 && (
									<div className="validation-error">
										{formik?.errors?.customer_type}
									</div>
								)}
							</div>
							<div className="input-wrapper">
								<label className="input-label">
									Customer Gender
								</label>
								<SelectBox
									name="customer_gender"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values?.customer_gender || ""}
									options={strings.gender}
									disabled={
										formik.values.customer_type === "business"
									}
								/>
								{formik.submitCount > 0 && (
									<div className="validation-error">
										{formik?.errors?.customer_gender}
									</div>
								)}
							</div>
						</div>
						<TextField
							name="customer_pan_vat"
							label="Customer PAN / VAT"
							placeholder="# Enter Pan / VAT"
							formik={formik}
						/>
						<TextField
							name="customer_address"
							label="Customer Address"
							placeholder="Enter Address"
							formik={formik}
						/>
						<div className="input-row-wrapper">
							<div className="input-wrapper">
								<label className="input-label">City</label>
								<SelectBox
									name="customer_city"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values?.customer_city || ""}
									options={strings.city}
								/>
								{formik.submitCount > 0 && (
									<div className="validation-error">
										{formik?.errors?.customer_city}
									</div>
								)}
							</div>
							{/* Never actually editable through this form (no
							customer_country field exists) — always a plain
							display, same as the view branch. */}
							<DisplayField label="Country" value="Nepal" />
						</div>
					</>
				)}
			</div>
		);
	};

	const _renderClientBills = () => {
		if (isLoading) {
			return <div>Loading....</div>;
		} else {
			if (
				!isEmptyObject(customerDetails) &&
				Array.isArray(customerDetails?.bills) &&
				customerDetails?.bills?.length > 0
			) {
				return (
					<div className="user-bills-container">
						<div className="section-header-row">
							<h2 className="section-header">Recent Bills</h2>
							<div
								className="section-header-btn"
								onClick={() =>
									handleViewAllBills(
										customerDetails?.customer_id,
									)
								}
							>
								View All
							</div>
						</div>
						<div className="bills-table-container">
							<div className="table-grid-container header">
								<div className="table-item-text align-left">
									<b>Invoice #</b>
								</div>
								<div className="table-item-text">
									<b>Invoice Date</b>
								</div>
								<div className="table-item-text align-right">
									<b>Amount</b>
								</div>
							</div>
							{customerDetails?.bills?.map((bill, i) => (
								<div key={i} className="table-grid-container">
									<div
										className="table-item-text align-left copy"
										onClick={() =>
											navigator.clipboard.writeText(
												bill?.invoice_number?.split(
													"_",
												)[2] || "",
											)
										}
									>
										{bill?.invoice_number}
									</div>
									<div className="table-item-text">
										{moment(bill?.invoice_date).format(
											"DD MMMM, YYYY",
										)}
										<span>
											{fromAdToBs(
												bill?.invoice_date,
												"DD MMMM, YYYY",
											)}
										</span>
									</div>
									<div className="table-item-text align-right">
										<div
											className={`bill-price ${
												bill?.bill_void && "void"
											}`}
										>
											<b>Rs. </b>
											{rupeeConverter(bill?.sub_total)}
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				);
			}
		}
	};

	const handleImageEdit = () => {
		if (clientEditable) {
			// Trigger the hidden file input element
			fileInputRef.current?.click();
		}
	};

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFile = e.target.files?.[0];
		// Check the file size
		if (selectedFile && selectedFile.size <= 2048576) {
			setSelectedImage(selectedFile);
		} else {
			// Handle the case where the file size exceeds the limit
			setImageSizeNotice("File size exceeds the limit");
		}
	};

	const imgSrc = useMemo(() => {
		if (selectedImage) {
			return URL.createObjectURL(selectedImage);
		} else if (!isEmptyObject(customer) && customer.customer_image) {
			return customer.customer_image;
		} else {
			return "";
		}
	}, [customer, selectedImage]);

	return (
		<StructuredSideModal
			open={isCustomerVisible}
			onOpenChange={(open) => {
				if (!open) onCustomerModalClose();
			}}
			title="Customer"
			width={560}
			// Only prompt to discard while actively editing with real pending
			// changes — view mode (clientEditable false) has nothing a user
			// could be discarding, and the success flash is its own state.
			isDirty={clientEditable && !showSuccess && formik.dirty}
			onSubmit={formik.handleSubmit}
			actions={
				!showSuccess &&
				(clientEditable ? (
					<Button
						variant="destructive"
						size="sm"
						type="button"
						onClick={handleEditCancel}
					>
						<FaEraser /> Cancel
					</Button>
				) : (
					<Button
						size="sm"
						type="button"
						onClick={() => setClientEditable(true)}
					>
						<FaPenToSquare /> Edit
					</Button>
				))
			}
			footer={
				clientEditable &&
				!showSuccess && (
					<div className="flex justify-end">
						<Button
							size="sm"
							type="submit"
							disabled={submitDisabled}
						>
							<FaFloppyDisk /> Save
						</Button>
					</div>
				)
			}
		>
			<div className="user-modal-container">
				<div className="modal-body">
					{showSuccess ? (
						isAddMode ? (
							<SuccessDisplay
								title={successTitle}
								description={successDescription}
							>
								<Button
									type="button"
									onClick={handleCompleteProfile}
								>
									Complete Profile
								</Button>
								<Button
									type="button"
									variant="secondary"
									onClick={handleSkipForNow}
								>
									Skip for now
								</Button>
							</SuccessDisplay>
						) : (
							<SuccessDisplay
								title={successTitle}
								description={successDescription}
							/>
						)
					) : (
						<>
							<FormError
								isError={
									uploadImageMutation.isError ||
									activeMutation.isError
								}
								error={
									uploadImageMutation.error ||
									activeMutation.error
								}
								fallback="Something went wrong. Please try again."
							/>
							{customer?.customer_id && (
								<div className="user-id">
									# {customer?.customer_id}
								</div>
							)}
							<div
								className={`user-img-container ${
									clientEditable && "editable"
								}`}
								onClick={handleImageEdit}
							>
								<div className="item_img">
									{imgSrc ? (
										<img
											crossOrigin="anonymous"
											src={imgSrc}
											alt="client"
										/>
									) : (
										<FaUser size="6em" />
									)}
								</div>
								{clientEditable ? (
									<div className="user-img-action">
										<FaPenToSquare />
									</div>
								) : (
									<div className="user-img-action active">
										<FaCheck />
									</div>
								)}
							</div>
							<input
								type="file"
								accept="image/*"
								ref={fileInputRef}
								style={{ display: "none" }}
								onChange={handleImageChange}
							/>
							<InlineNotice
								message={imageSizeNotice}
								type="warning"
								onDismiss={() => setImageSizeNotice(null)}
							/>
							<div className="divider" />
							{_renderClientInfo()}
							{!clientEditable && customer.company_customer_id !== "" && (
								<>
									<div className="divider" />
									{_renderClientBills()}
								</>
							)}
						</>
					)}
				</div>
			</div>
		</StructuredSideModal>
	);
}

export default memo(CustomerProfile);
